// src/activities/cape-vr/cape-vr-views/cape-vr-files/cape-vr-ratings-files/RatingsTable.jsx

import { useTranslation } from 'react-i18next';
import DataTable from '../../../../../components/feedback/DataTable';
import CommentCell from '../../../../../components/feedback/CommentCell';
import { DataTable as SharedDataTable } from '../../../../../components/data-display';
import { buildInstrumentResponsePresentation } from '../../../../../utils/instrumentResponsePresentation';
import { variableTranslations } from '../../../../../constants/variableTranslations';

function NormalizedSection({ title, rows, columns }) {
  if (!rows.length) return null;
  return <>
    <h4>{title}</h4>
    <SharedDataTable
      columns={columns}
      rows={rows}
      rowKey={(row, index) => `${row.attribute}-${index}`}
    />
  </>;
}

function RatingsTable({ submittedData }) {
  const { t } = useTranslation();

  if (!submittedData) {
    return <p>{t('no_ratings_available')}</p>;
  }

  const presentation = buildInstrumentResponsePresentation(submittedData);
  if (presentation.kind === 'normalized' && presentation.protocolKey === 'cape-vr') {
    const units = presentation.units;
    const displayLabel = key => t(variableTranslations[key] || key);
    const numericRows = units
      .filter(unit => ['numeric', 'customNumeric'].includes(unit.kind) && unit.responseState === 'answered')
      .map(unit => ({ attribute: displayLabel(unit.key) === unit.key ? unit.label : displayLabel(unit.key), userRating: `${unit.value}/100` }));
    const categoryRows = units
      .filter(unit => unit.kind === 'category' && unit.responseState === 'answered')
      .map(unit => ({ attribute: displayLabel(unit.key), userRating: t(unit.category), userComment: unit.comment || '-' }));
    const inconsistency = units.find(unit => unit.kind === 'inconsistency');
    const inconsistencyNoneRows = inconsistency?.responseState === 'none'
      ? [{ attribute: t('inconsistencies'), userRating: t('none') }]
      : [];
    const inconsistencyTaskRows = inconsistency?.responseState === 'present'
      ? (inconsistency.affectedTasks || []).map(task => ({
          attribute: t(task.taskKey),
          description: task.description,
        }))
      : [];
    const optionalGroupRow = (key, labelKey) => {
      const unit = units.find(candidate => candidate.key === key);
      const selections = (unit?.selected || []).map(selected =>
        selected === 'other' ? unit.otherLabel : displayLabel(selected));
      return selections.length ? [{ group: t(labelKey), selections: selections.join('; ') }] : [];
    };
    const impression = units.find(unit => unit.key === 'overallImpression');
    const numericColumns = [
      { key: 'attribute', label: t('rating_item'), width: '50%' },
      { key: 'userRating', label: t('your_rating'), width: '50%' },
    ];
    const categoryColumns = [
      { key: 'attribute', label: t('rating_item'), width: '33%' },
      { key: 'userRating', label: t('your_rating'), width: '33%' },
      { key: 'userComment', label: t('your_comment'), width: '34%' },
    ];
    const optionalGroupColumns = [
      { key: 'group', label: t('response_group'), width: '35%' },
      { key: 'selections', label: t('selections'), width: '65%' },
    ];
    const taskColumns = [
      { key: 'attribute', label: t('task'), width: '40%' },
      { key: 'description', label: t('description'), width: '60%' },
    ];

    return <div className="ratings-report">
      <NormalizedSection title={t('vas_rating_results')} rows={numericRows} columns={numericColumns} />
      <NormalizedSection title={t('cape_vr_descriptive_ratings')} rows={categoryRows} columns={categoryColumns} />
      <NormalizedSection title={t('inconsistencies')} rows={inconsistencyNoneRows} columns={numericColumns} />
      <NormalizedSection title={t('inconsistencies')} rows={inconsistencyTaskRows} columns={taskColumns} />
      <NormalizedSection
        title={t('cape_vr_optional_observations')}
        rows={[
          ...optionalGroupRow('instabilities', 'instabilities'),
          ...optionalGroupRow('additionalFeatures', 'additional_features'),
        ]}
        columns={optionalGroupColumns}
      />
      {impression?.responseState === 'answered' && <>
        <h4>{t('overall_impression')}</h4>
        <SharedDataTable
          columns={[{ key: 'comment', label: '', width: '100%' }]}
          rows={[{ comment: impression.comment }]}
          rowKey="comment"
          showHeader={false}
        />
      </>}
    </div>;
  }

  if (!submittedData.perceptualData) return <p>{t('no_ratings_available')}</p>;

  const userData = submittedData.perceptualData;

  return (
    <div className="ratings-report">
      <h4>{t('standard_rating_items')}</h4>
      <DataTable
        userData={userData}
        attributeLabel={t('rating_item')}
        attributeKeys={['overallSeverity', 'roughness', 'breathiness', 'strain']}
      />

      <h4>{t('pitch_and_loudness')}</h4>
      <DataTable
        userData={userData}
        attributeLabel={t('rating_item')}
        attributeKeys={['normalPitch', 'tooLowPitch', 'tooHighPitch', 'normalLoudness', 'tooQuietLoudness', 'tooLoudLoudness']}
      />

      <h4>{t('resonance_and_nasality')}</h4>
      <DataTable
        userData={userData}
        attributeLabel={t('rating_item')}
        attributeKeys={['normalResonance', 'frontResonance', 'backResonance', 'normalNasality', 'hyponasality', 'hypernasality']}
      />

      <h4>{t('inconsistencies')}</h4>
      <DataTable
        userData={userData}
        attributeLabel={t('rating_item')}
        attributeKeys={['vowelsInconsistency', 'sentencesInconsistency', 'extemporaneousSpeechInconsistency']}
      />

      <h4>{t('instabilities')}</h4>
      <DataTable
        userData={userData}
        attributeLabel={t('rating_item')}
        attributeKeys={['aphonicBreak', 'pitchBreak', 'pitchInstability', 'spasm', 'tremor', 'otherInstability']}
      />

      <h4>{t('additional_features')}</h4>
      <DataTable
        userData={userData}
        attributeLabel={t('rating_item')}
        attributeKeys={['aphonia', 'asthenia', 'diplophonia', 'falsetto', 'fry', 'hardGlottalAttack', 'wetGurgly', 'otherAdditionalFeature']}
      />

      <h4>{t('overall_impression')}</h4>
      <CommentCell userData={userData} attributeKeys={['overallImpression']} />
    </div>
  );
}

export default RatingsTable;
